import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { CircularProgress } from "@mui/material";
import { X } from "lucide-react";
import Filters from "../../../../assets/images/filters.svg";
import Threedots from "../../../../assets/images/PhosphorLine.svg";
import Refresh from "../../../../assets/images/ArrowCounterClockwise.svg";
import MultiSelectWithSearchInput from "Samarth Module/components/dropdown/MultiSelectWithInput";
import {
  fetchSmartSourceLensFilters,
  fetchSmartSourceLensRows,
  fetchSmartSourceLensCount,
  fetchSmartSourceLensTotals,
  fetchSmartSourceLensKpi,
  fetchSmartScourceLensPdqAllocationTable,
  fetchSmartScourceLensHistoricalGraph,
  fetchSmartScourceLensExportData,
  fetchDcSupplyInsightsRows,
  fetchDcSupplyInsightsCount,
  fetchDcSupplyInsightsTotals,
  fetchDcSupplyInsightsFilters,
  fetchDcSupplyInsightsExportData,
  fetchDcSupplyInsightsHistoricalGraph,
} from "../../../api/factoryToDcApi";
import OverflowMenu from "Samarth Module/components/overflowMenu/OverflowMenu";
import MoreFilters from "Samarth Module/components/drawers/moreFilters";
import CustomiseColumns from "Samarth Module/components/drawers/CustomiseColumns";
import SnackBarMessage from "Samarth Module/components/snackbar/snackbar";
import styles from "./SmartSourceLens.module.css";
import newExcel from "../../../../assets/images/FileXls.svg";
import Expand from "../../../../assets/images/expand.svg";
import CustomButton from "Samarth Module/components/buttons/CustomButton";
import SmartSourceLensOverviewTable from "./SmartSourceLensOverviewTable";
import SmartSourceLensKpi from "./SmartSourceLensKpi";
import BucketsOfAllocation from "./BucketsOfAllocation";
import HistoricalDataGraph from "./HistoricalDataGraph";
import WeightageTopNDialog from "./WeightageTopNDialog";

const DEFAULT_ROWS_PER_PAGE = 15;
const PDQ_PAGE_SIZE = 15;
const DEFAULT_WEIGHTAGE = {
  Volume: "0",
  Value: "100",
};

const DEFAULT_PDQ_SORTING = Object.freeze({
  sortBy: "BG",
  sortOrder: "ASC",
});

const FILTER_SHIMMER_KEYS = Object.freeze([
  "filter-shimmer-1",
  "filter-shimmer-2",
  "filter-shimmer-3",
  "filter-shimmer-4",
  "filter-shimmer-5",
  "filter-shimmer-6",
  "filter-shimmer-7",
  "filter-shimmer-8",
]);

const WEIGHTAGE_ENABLED_TABS = new Set(["Manual Indent", "Production Plan"]);

const DEFAULT_PDQ_OPTIONS = Object.freeze({
  append: false,
  cursor: null,
});

const DEFAULT_LOAD_ALL_APIS_OPTIONS = Object.freeze({
  skipHeavyApis: false,
  skipFilters: false,
  skipKpi: false,
});

// Map UI tab labels to backend API values
const TAB_TO_BACKEND_MAP = Object.freeze({
  "CBU-DC-Source": "Summary",
  "CBU-Source": "Summary",
  "Production Plan": "Production Plan",
  "Manual Indent": "Manual Indent",
  "Quality Pending": "Quality Pending",
});

// Helper to get backend value for a given UI tab
const getBackendTabValue = (uiTab) => TAB_TO_BACKEND_MAP[uiTab] || uiTab;

// Helper to get filter preference type for saveFilterPreferences API
const getFilterPreferenceType = (activeTab) => {
  if (activeTab === "CBU-DC-Source") {
    return "Dc Supply Insights (Summary)";
  }
  if (activeTab === "CBU-Source") {
    return "Smart Source Lens (Summary)";
  }
  // Keep existing functionality for Production Plan and Manual Indent
  return `Smart Source Lens (${activeTab})`;
};

const isAbortError = (error) =>
  error?.name === "AbortError" ||
  error?.message?.toLowerCase?.().includes("aborted");

const getFiltersForTab = (activeTab, sourceFilters = []) =>
  sourceFilters.filter(
    (filter) => activeTab === "Quality Pending" || filter?.label !== "Status",
  );

const buildSelectedFilterPayloadForTab = (activeTab, sourceFilters = []) =>
  getFiltersForTab(activeTab, sourceFilters).reduce((acc, filter) => {
    acc[filter.label] = filter.selectedData;
    return acc;
  }, {});

const buildSelectedFilterPayload = (sourceFilters = []) =>
  sourceFilters.reduce((acc, filter) => {
    acc[filter.label] = filter.selectedData;
    return acc;
  }, {});

const buildSupportingApiFilterPayload = (
  activeTab,
  sourceFilters = [],
) => {
  const payload = buildSelectedFilterPayloadForTab(
    activeTab,
    sourceFilters,
  );

  // For CBU-DC-Source tab, exclude the DC filter from the payload
  if (activeTab === "CBU-DC-Source") {
    const { DC, ...filtersWithoutDc } = payload;
    return filtersWithoutDc;
  }

  if (activeTab !== "Quality Pending") {
    return payload;
  }

  const { Status, ...filtersWithoutStatus } = payload;
  return filtersWithoutStatus;
};

// Helper to build filters for DC Supply Insights APIs (includes DC filter)
const buildDcSupplyInsightsFilterPayload = (
  sourceFilters = [],
) => {
  const payload = buildSelectedFilterPayloadForTab(
    "CBU-DC-Source",
    sourceFilters,
  );
  // For DC Supply Insights APIs, we include ALL filters including DC
  return payload;
};

const getSelectedValues = (selectedData) => {
  if (Array.isArray(selectedData)) {
    return selectedData;
  }

  if (selectedData) {
    return [selectedData];
  }

  return [];
};

const getTotalRow = (totalsData) => {
  if (!totalsData) {
    return {};
  }

  if (Array.isArray(totalsData?.total_data)) {
    return totalsData.total_data[0] || {};
  }

  return totalsData?.total_data || {};
};

const handleOptionalApiError = (error, apiName) => {
  if (isAbortError(error)) {
    return null;
  }

  console.error(`${apiName} API error:`, error);
  return undefined;
};

const shouldSendWeightagePayload = (activeTab, isWeightageSubmitted) =>
  WEIGHTAGE_ENABLED_TABS.has(activeTab) && isWeightageSubmitted;

const createDataFetchPromises = (isDcSupplyInsights, payload, signal) => {
  let rowsPromise;
  if (isDcSupplyInsights) {
    rowsPromise = fetchDcSupplyInsightsRows(payload, signal);
  } else {
    rowsPromise = fetchSmartSourceLensRows(payload, signal);
  }

  let countPromise;
  if (isDcSupplyInsights) {
    countPromise = fetchDcSupplyInsightsCount(payload, signal).catch((error) =>
      handleOptionalApiError(error, "Count"),
    );
  } else {
    countPromise = fetchSmartSourceLensCount(payload, signal).catch((error) =>
      handleOptionalApiError(error, "Count"),
    );
  }

  let totalsPromise;
  if (isDcSupplyInsights) {
    totalsPromise = fetchDcSupplyInsightsTotals(payload, signal).catch(
      (error) => handleOptionalApiError(error, "Totals"),
    );
  } else {
    totalsPromise = fetchSmartSourceLensTotals(payload, signal).catch((error) =>
      handleOptionalApiError(error, "Totals"),
    );
  }

  return { rowsPromise, countPromise, totalsPromise };
};

const buildTablePayload = ({
  activeAccount,
  activeTab,
  sourceFilters,
  uom,
  page,
  rowsPerPage,
  sortBy,
  sortOrder,
  fieldsection,
  weightage,
  topNValue,
  isWeightageSubmitted,
}) => {
  const isDcSupplyInsights = activeTab === "CBU-DC-Source";

  const payload = {
    email: activeAccount.username,
    uom,
    filters: buildSelectedFilterPayloadForTab(activeTab, sourceFilters),
    type: isDcSupplyInsights ? "Dc Supply Insights" : "Smart Source Lens",
    overviewtype: isDcSupplyInsights
      ? "Summary"
      : getBackendTabValue(activeTab),
    pagination: {
      page,
      rowsPerPage: rowsPerPage || DEFAULT_ROWS_PER_PAGE,
    },
    sorting: {
      sortBy,
      sortOrder,
      fieldsection,
    },
  };

  if (shouldSendWeightagePayload(activeTab, isWeightageSubmitted)) {
    payload["Weightage Percentage"] = {
      Volume: Number(weightage.Volume),
      Value: Number(weightage.Value),
    };
    payload.top_n = Number(topNValue);
  }

  return payload;
};

const OverviewTabs = ({ selectedTab, onTabChange }) => {
  const tabs = [
    "CBU-DC-Source",
    "CBU-Source",
    "Manual Indent",
    "Quality Pending",
    "Production Plan",
  ];

  return (
    <div
      style={{
        display: "flex",
        gap: "10px",
        padding: "5px",
        border: "1px solid #E5E7EB",
        borderRadius: "10px",
        backgroundColor: "rgb(246 246 246)",
      }}
    >
      {tabs.map((tab) => (
        <button
          key={tab}
          type="button"
          onClick={() => onTabChange(tab)}
          style={{
            padding: "6px 14px",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "12px",
            border:
              selectedTab === tab ? "1px solid #3447d4" : "1px solid #E5E7EB",
            background: selectedTab === tab ? "#EEF2FF" : "#fff",
            color: selectedTab === tab ? "#3447d4" : "#555",
            fontWeight: selectedTab === tab ? 600 : 400,
          }}
        >
          {tab}
        </button>
      ))}
    </div>
  );
};

OverviewTabs.propTypes = {
  selectedTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
};

const SmartSourceLens = ({ sideViewTab }) => {
  // ---------------------------------------------------------------
  // Merge helper for the /smartsourcelens_filters response.
  //
  // The backend recomputes each filter's `selectedData` as
  //   SELECT DISTINCT [col] WHERE (all filters incl. this one)
  // which returns AT MOST the values the user picked - often fewer
  // when a large filter combination has an empty/small intersection
  // in the fact table. If we replace state directly with the API
  // response, chips vanish from the UI even though the user *did*
  // pick them (they just don't have any matching rows right now).
  //
  // To keep the chips accurate to the user's intent, we take:
  //   * `data`, `isFavourite`, `DisplayOrder`, `label` from the
  //      server (authoritative - depends on other filters).
  //   * `selectedData`  from the PREVIOUS local state whenever we
  //      already have it (the user's actual selections).
  //
  // Any incoming filter that wasn't present locally falls through
  // to the server payload unchanged (safe default). Filters that
  // exist locally but are missing from the response are dropped -
  // the server is the source of truth for which filters exist.
  // ---------------------------------------------------------------
  const mergeFiltersResponse = (incoming, previous) => {
    const incomingList = Array.isArray(incoming) ? incoming : [];
    if (!Array.isArray(previous) || previous.length === 0) {
      return incomingList;
    }
    const prevByLabel = new Map(previous.map((f) => [f?.label, f]));
    return incomingList.map((f) => {
      const prev = prevByLabel.get(f?.label);
      if (!prev) return f;
      return {
        ...f,
        selectedData: prev.selectedData ?? f.selectedData,
      };
    });
  };

  const abortControllersRef = useRef({
    kpi: null,
    table: null,
    pdq: null,
    historical: null,
    filters: null,
  });

  const cancelPrevious = (key) => {
    if (abortControllersRef.current[key]) {
      abortControllersRef.current[key].abort();
    }
    abortControllersRef.current[key] = new AbortController();
    return abortControllersRef.current[key].signal;
  };

  // ---------------------------------------------------------------
  // Debounced trigger for `loadAllApis`.
  //
  // Why: `handleFilterChange` / `handleRemoveSelectedValue` are called
  // synchronously for every dropdown pick / chip removal. If the user
  // selects 5 values in quick succession we would fire 5 full
  // request-fans (up to ~5 heavy APIs each = ~25 concurrent server
  // handlers) even though only the last state matters.
  //
  // `AbortController` cancels the client-side fetch, but the Flask
  // handler that was already dispatched keeps running the SQL to
  // completion - so the DB connection pool gets flooded with
  // effectively-orphaned work and every subsequent request queues
  // behind it, which is why users see the response time climb
  // exactly when they interact fastest. "Save preferences" flows feel
  // fast because they only produce a single trigger, not a burst.
  //
  // Debouncing on the client is the actual fix: we collapse the burst
  // into ONE server-side execution once the user pauses. The existing
  // AbortController still cancels the previous in-flight call if the
  // user resumes editing before it finishes.
  // ---------------------------------------------------------------
  const pendingLoadAllApisRef = useRef(null);
  const LOAD_ALL_APIS_DEBOUNCE_MS = 800;

  const pendingKpiTimerRef = useRef(null);
  const pendingHeavyApisTimerRef = useRef(null);
  const latestLoadIntentRef = useRef(0);

  const clearDeferredApiTimers = () => {
    if (pendingKpiTimerRef.current) {
      clearTimeout(pendingKpiTimerRef.current);
      pendingKpiTimerRef.current = null;
    }

    if (pendingHeavyApisTimerRef.current) {
      clearTimeout(pendingHeavyApisTimerRef.current);
      pendingHeavyApisTimerRef.current = null;
    }
  };

  const scheduleKpiApi = (currentFilters, uom, loadIntentId) => {
    if (pendingKpiTimerRef.current) {
      clearTimeout(pendingKpiTimerRef.current);
      pendingKpiTimerRef.current = null;
    }

    pendingKpiTimerRef.current = setTimeout(() => {
      pendingKpiTimerRef.current = null;

      if (loadIntentId !== latestLoadIntentRef.current) return;

      fetchKpiData(currentFilters, uom);
    }, 250);
  };

  const scheduleHeavyApis = (currentFilters, uom, loadIntentId) => {
    if (pendingHeavyApisTimerRef.current) {
      clearTimeout(pendingHeavyApisTimerRef.current);
      pendingHeavyApisTimerRef.current = null;
    }

    pendingHeavyApisTimerRef.current = setTimeout(() => {
      pendingHeavyApisTimerRef.current = null;

      if (loadIntentId !== latestLoadIntentRef.current) return;

      fetchPdqAllocationData(currentFilters, uom);
      fetchHistoricalGraphData(currentFilters, uom);
    }, 1200);
  };

  const scheduleLoadAllApis = (
    currentFilters,
    uom,
    options,
    delayMs = LOAD_ALL_APIS_DEBOUNCE_MS,
  ) => {
    if (pendingLoadAllApisRef.current) {
      clearTimeout(pendingLoadAllApisRef.current);
      pendingLoadAllApisRef.current = null;
    }

    clearDeferredApiTimers();

    if (!delayMs) {
      loadAllApis(currentFilters, uom, options);
      return;
    }

    pendingLoadAllApisRef.current = setTimeout(() => {
      pendingLoadAllApisRef.current = null;
      loadAllApis(currentFilters, uom, options);
    }, delayMs);
  };

  const requestRefs = {
    kpi: useRef(0),
    table: useRef(0),
    pdq: useRef(0),
    historical: useRef(0),
    filters: useRef(0),
  };

  const { accounts } = useMsal();
  const activeAccount = accounts[0];

  const [isDrawerOpen, setIsDrawerOpen] = useState({
    filterDrawer: false,
    customiseDrawer: false,
  });
  const [tableData, setTableData] = useState([]);

  const [paginationData, setPaginationData] = useState(null);
  const [sortingData, setSortingData] = useState({
    sortBy: "",
    sortOrder: "DESC",
    fieldsection: "",
  });

  const [filters, setFilters] = useState({
    smartSourceLensFiltersData: [],

    smartSourceLensColumnsMap: {},
    customizedColumnsMap: {},
  });

  const [selectedUOM, setSelectedUOM] = useState("Tonnes");

  const [loading, setLoading] = useState({
    initialLoading: true,
    dataLoading: false,
    tableBodyLoading: false,
    exportLoading: false,
    summaryLoading: false,
  });

  const [filtersLoading, setFiltersLoading] = useState(false);

  const [snackBarObject, setSnackBarObject] = useState({
    snackbarOpen: false,
    message: "",
    type: "error",
  });

  const [showExpanded, setShowExpanded] = useState(true);
  const [expandedGraph, setExpandedGraph] = useState(null);

  // UOM options
  const uomOptions = ["Cases", "Tonnes", "Eaches"];

  const [activeTab, setActiveTab] = useState("CBU-DC-Source");

  const [kpiData, setKpiData] = useState([]);
  const [kpiLoading, setKpiLoading] = useState(false);

  const [pdqTableData, setPdqTableData] = useState([]);
  const [pdqLoading, setPdqLoading] = useState(false);
  const [pdqLoadMoreLoading, setPdqLoadMoreLoading] = useState(false);
  const [pdqHasMore, setPdqHasMore] = useState(false);
  const [pdqSortingData, setPdqSortingData] = useState(DEFAULT_PDQ_SORTING);

  const pdqNextCursorRef = useRef(null);
  const pdqHasMoreRef = useRef(false);
  const pdqLoadMoreRef = useRef(false);

  const [historicalData, setHistoricalData] = useState([]);
  const [historicalLoading, setHistoricalLoading] = useState(false);

  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
  const [fallbackColumns, setFallbackColumns] = useState([]);

  const [openWeightageDialog, setOpenWeightageDialog] = useState(false);
  const [weightageError, setWeightageError] = useState("");
  const [dialogLoading, setDialogLoading] = useState(false);
  const [weightage, setWeightage] = useState(DEFAULT_WEIGHTAGE);
  const [topNValue, setTopNValue] = useState(15);
  const weightageSubmittedRef = useRef(false);

  const previousWeightageRef = useRef(DEFAULT_WEIGHTAGE);
  const previousTopNValueRef = useRef(15);

  // Remember the previously active tab so we can decide whether the
  // filters API needs to be re-fetched on tab change. Rule: the filters
  // API is called when either the new tab OR the previous tab is
  // "CBU-DC-Source" (uses fetchDcSupplyInsightsFilters) or "Manual Indent"
  // (different source table / adds the DC filter).
  const prevActiveTabRef = useRef("CBU-DC-Source");

  const handleWeightageDialogOpen = () => {
    previousWeightageRef.current = { ...weightage };
    previousTopNValueRef.current = topNValue;
    setWeightageError("");
    setOpenWeightageDialog(true);
  };

  const handleWeightageDialogClose = () => {
    setWeightage({ ...previousWeightageRef.current });
    setTopNValue(previousTopNValueRef.current);
    setWeightageError("");
    setOpenWeightageDialog(false);
  };

  const validateWeightage = (data, currentTopN = topNValue) => {
    const volume = String(data.Volume).trim();
    const value = String(data.Value).trim();
    const topN = String(currentTopN).trim();

    if (!volume) {
      setWeightageError("Volume can't be empty");
      return false;
    }

    if (!value) {
      setWeightageError("Value can't be empty");
      return false;
    }

    if (Number(volume) + Number(value) !== 100) {
      setWeightageError("Sum of Volume and Value must be 100%");
      return false;
    }

    if (!topN) {
      setWeightageError("Top N can't be empty");
      return false;
    }

    if (Number(topN) <= 0) {
      setWeightageError("Top N must be greater than 0");
      return false;
    }

    if (Number(topN) > 1000) {
      setWeightageError("Top N value is too large");
      return false;
    }

    setWeightageError("");
    return true;
  };

  const handleWeightageSubmit = async () => {
    if (!validateWeightage(weightage)) return;
    setDialogLoading(true);
    try {
      weightageSubmittedRef.current = true;
      await fetchData(
        filters.smartSourceLensFiltersData,
        1,
        "",
        "DESC",
        selectedUOM,
        paginationData?.rowsPerPage || 15,
        "",
      );
      setOpenWeightageDialog(false);
    } catch (err) {
      console.error("Weightage submit error", err);
    } finally {
      setDialogLoading(false);
    }
  };

  const getKpiOverviewType = (tab) =>
    tab === "CBU-DC-Source" ? "Summary" : getBackendTabValue(tab);

  const fetchKpiData = async (
    SmartSourceLensFilters = [],
    uom = selectedUOM,
  ) => {
    const requestId = ++requestRefs.kpi.current;
    setKpiLoading(true);

    try {
      const signal = cancelPrevious("kpi");
      const kpiOverviewType = getKpiOverviewType(activeTab);
      const payload = {
        email: activeAccount.username,
        uom,
        filters: buildSupportingApiFilterPayload(
          activeTab,
          SmartSourceLensFilters,
        ),
        type: "Smart Source Lens",
        overviewtype: kpiOverviewType,
      };

      const res = await fetchSmartSourceLensKpi(payload, signal);
      if (requestId !== requestRefs.kpi.current) return;
      setKpiData(res?.data || []);
    } catch (e) {
      if (e.name !== "AbortError") {
        console.error("KPI fetch error", e);
      }
    } finally {
      if (requestId === requestRefs.kpi.current) {
        setKpiLoading(false);
      }
    }
  };

  const fetchPdqAllocationData = async (
    SmartSourceLensFilters = [],
    uom = selectedUOM,
    options = DEFAULT_PDQ_OPTIONS,
  ) => {
    const isAppend = Boolean(options.append);
    const requestId = ++requestRefs.pdq.current;

    if (isAppend) {
      if (pdqLoadMoreRef.current) return;
      if (!pdqHasMoreRef.current) return;

      pdqLoadMoreRef.current = true;
      setPdqLoadMoreLoading(true);
    } else {
      setPdqLoading(true);

      // Reset PDQ infinite-scroll state on fresh load
      setPdqTableData([]);
      setPdqHasMore(false);

      pdqHasMoreRef.current = false;
      pdqNextCursorRef.current = null;
    }

    try {
      const signal = cancelPrevious("pdq");

      const safeSorting = options.sorting || pdqSortingData;

      const payload = {
        email: activeAccount.username,
        uom,
        filters: buildSupportingApiFilterPayload(
          activeTab,
          SmartSourceLensFilters,
        ),
        type: "Smart Source Lens",
        page_size: PDQ_PAGE_SIZE,
        cursor: isAppend ? options.cursor : null,
        sorting: {
          sortBy: safeSorting.sortBy,
          sortOrder: safeSorting.sortOrder,
        },
      };

      const res = await fetchSmartScourceLensPdqAllocationTable(
        payload,
        signal,
      );

      if (requestId !== requestRefs.pdq.current) return;

      const newRows = res?.data || [];
      const nextCursor = res?.next_cursor || null;
      const hasMore = Boolean(res?.has_more);

      setPdqTableData((prev) => {
        if (!isAppend) return newRows;

        // Optional small duplicate protection using BG + Small C + Source
        const existingKeys = new Set(
          prev.map(
            (row) =>
              `${row?.BG || ""}__${row?.["Small C"] || ""}__${row?.Source || ""}`,
          ),
        );

        const uniqueNewRows = newRows.filter((row) => {
          const key = `${row?.BG || ""}__${row?.["Small C"] || ""}__${row?.Source || ""}`;
          return !existingKeys.has(key);
        });

        return [...prev, ...uniqueNewRows];
      });

      setPdqHasMore(hasMore);

      pdqHasMoreRef.current = hasMore;
      pdqNextCursorRef.current = nextCursor;
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("PDQ Allocation API error:", error);
      }
    } finally {
      if (requestId === requestRefs.pdq.current) {
        if (isAppend) {
          pdqLoadMoreRef.current = false;
          setPdqLoadMoreLoading(false);
        } else {
          setPdqLoading(false);
        }
      }
    }
  };

  const handlePdqLoadMore = useCallback(() => {
    if (pdqLoadMoreRef.current) return;
    if (!pdqHasMoreRef.current) return;
    if (!pdqNextCursorRef.current) return;

    fetchPdqAllocationData(filters.smartSourceLensFiltersData, selectedUOM, {
      append: true,
      cursor: pdqNextCursorRef.current,
      sorting: pdqSortingData,
    });
  }, [filters.smartSourceLensFiltersData, selectedUOM, pdqSortingData]);

  const handlePdqSortChange = useCallback(
    (sortBy) => {
      const isActiveColumn = pdqSortingData.sortBy === sortBy;
      const nextSortOrder =
        isActiveColumn && pdqSortingData.sortOrder === "ASC" ? "DESC" : "ASC";

      const nextSorting = {
        sortBy,
        sortOrder: nextSortOrder,
      };

      setPdqSortingData(nextSorting);

      fetchPdqAllocationData(filters.smartSourceLensFiltersData, selectedUOM, {
        append: false,
        cursor: null,
        sorting: nextSorting,
      });
    },
    [filters.smartSourceLensFiltersData, pdqSortingData, selectedUOM],
  );

  const fetchHistoricalGraphData = async (
    SmartSourceLensFilters = [],
    uom = selectedUOM,
  ) => {
    const requestId = ++requestRefs.historical.current;
    setHistoricalLoading(true);

    try {
      const signal = cancelPrevious("historical");

      const isDcSupplyInsights = activeTab === "CBU-DC-Source";

      const payload = {
        email: activeAccount.username,
        uom,
        filters: isDcSupplyInsights
          ? buildDcSupplyInsightsFilterPayload(SmartSourceLensFilters)
          : buildSupportingApiFilterPayload(
              activeTab,
              SmartSourceLensFilters,
            ),
      };

      const response = isDcSupplyInsights
        ? await fetchDcSupplyInsightsHistoricalGraph(payload, signal)
        : await fetchSmartScourceLensHistoricalGraph(payload, signal);

      if (requestId !== requestRefs.historical.current) {
        return;
      }

      setHistoricalData(response?.data || []);
    } catch (error) {
      if (!isAbortError(error)) {
        console.error("Historical Graph API error:", error);
      }
    } finally {
      if (requestId === requestRefs.historical.current) {
        setHistoricalLoading(false);
      }
    }
  };

  const fetchData = async (
    SmartSourceLensFilters = [],
    page = 1,
    sortBy = "",
    sortOrder = "",
    uom = selectedUOM,
    rowsPerPage = DEFAULT_ROWS_PER_PAGE,
    fieldsection = "",
  ) => {
    const requestId = ++requestRefs.table.current;
    const isFilterChange = page === 1 && sortBy === "";
    const isDcSupplyInsights = activeTab === "CBU-DC-Source";

    setLoading((prev) => ({
      ...prev,
      dataLoading: isFilterChange,
      tableBodyLoading: !isFilterChange,
      summaryLoading: true,
    }));

    try {
      const signal = cancelPrevious("table");

      const payload = buildTablePayload({
        activeAccount,
        activeTab,
        sourceFilters: SmartSourceLensFilters,
        uom,
        page,
        rowsPerPage,
        sortBy,
        sortOrder,
        fieldsection,
        weightage,
        topNValue,
        isWeightageSubmitted: weightageSubmittedRef.current,
      });

      const { rowsPromise, countPromise, totalsPromise } =
        createDataFetchPromises(isDcSupplyInsights, payload, signal);

      let rowsData;

      try {
        rowsData = await rowsPromise;
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        throw error;
      }

      if (requestId !== requestRefs.table.current) {
        return;
      }

      if (rowsData?.defaultColumns) {
        setFallbackColumns(rowsData.defaultColumns);
      }

      const rows = rowsData?.data || [];

      setSortingData({
        sortBy: rowsData?.sorting?.sortBy || sortBy,
        sortOrder: rowsData?.sorting?.sortOrder || sortOrder,
        fieldsection: rowsData?.sorting?.fieldsection || fieldsection,
      });

      setTableData([{}, ...rows]);

      setLoading((prev) => ({
        ...prev,
        dataLoading: false,
        tableBodyLoading: false,
      }));

      const [countData, totalsData] = await Promise.all([
        countPromise,
        totalsPromise,
      ]);

      if (requestId !== requestRefs.table.current) {
        return;
      }

      if (countData?.pagination) {
        setPaginationData(countData.pagination);
      }

      setTableData([getTotalRow(totalsData), ...rows]);
    } catch (error) {
      if (!isAbortError(error)) {
        console.error("Error fetching data:", error);
      }

      if (requestId === requestRefs.table.current) {
        setTableData([]);
      }
    } finally {
      if (requestId === requestRefs.table.current) {
        setLoading((prev) => ({
          ...prev,
          dataLoading: false,
          tableBodyLoading: false,
          summaryLoading: false,
        }));
      }
    }
  };
  useEffect(() => {
    if (!activeAccount?.username) return;

    loadAllApis([], selectedUOM, {
      skipHeavyApis: false,
      skipFilters: false,
    });
  }, []);

  // Cancel any pending debounced `loadAllApis` when the component
  // unmounts so an in-flight timeout can't fire after the user has
  // navigated away.
  useEffect(() => {
    return () => {
      if (pendingLoadAllApisRef.current) {
        clearTimeout(pendingLoadAllApisRef.current);
        pendingLoadAllApisRef.current = null;
      }

      clearDeferredApiTimers();

      Object.values(abortControllersRef.current).forEach((controller) => {
        if (controller) {
          controller.abort();
        }
      });
    };
  }, []);

  useEffect(() => {
    if (!activeAccount?.username) return;

    // Prevent duplicate call on initial render
    if (!isInitialLoadDone) return;

    // Only process if tab actually changed (not just isInitialLoadDone state change)
    const prevTab = prevActiveTabRef.current;
    if (activeTab === prevTab) return;

    // Re-fetch the filters API when:
    // 1. Transitioning to/from "CBU-DC-Source" (uses fetchDcSupplyInsightsFilters)
    // 2. Transitioning to/from "Manual Indent" (different source table)
    // 3. Selecting "Quality Pending" (requires filters API for new tab)
    const isDcSupplyInsightsInvolved =
      activeTab === "CBU-DC-Source" || prevTab === "CBU-DC-Source";
    const isManualIndentInvolved =
      activeTab === "Manual Indent" || prevTab === "Manual Indent";
    const isQualityPendingInvolved =
      activeTab === "Quality Pending" || prevTab === "Quality Pending";

    const shouldFetchFilters =
      isDcSupplyInsightsInvolved ||
      isManualIndentInvolved ||
      isQualityPendingInvolved;

    const shouldFetchHistoricalGraph =
      activeTab === "CBU-DC-Source" || prevTab === "CBU-DC-Source";
    // Reset weightage state on tab change so top_n/weightage don't
    // carry over between tabs
    weightageSubmittedRef.current = false;
    setWeightage(DEFAULT_WEIGHTAGE);
    setTopNValue(15);
    setWeightageError("");

    scheduleLoadAllApis(
      filters.smartSourceLensFiltersData,
      selectedUOM,
      {
        skipHeavyApis: true,
        skipFilters: !shouldFetchFilters,
        skipKpi: false,
        showFilterShimmer: shouldFetchFilters,
      },
      150,
    );

    if (shouldFetchHistoricalGraph) {
      fetchHistoricalGraphData(filters.smartSourceLensFiltersData, selectedUOM);
    }
    prevActiveTabRef.current = activeTab;
  }, [activeTab, isInitialLoadDone]);

  // Handle page change and sorting
  const handlePageChange = (
    newPage,
    sortCol,
    sortDir,
    rowsPerPage,
    fieldsection = "",
  ) => {
    const sortBy = sortCol || sortingData.sortBy;
    const sortOrder = sortDir || sortingData.sortOrder;
    setSortingData({ sortBy, sortOrder, fieldsection: fieldsection });
    setPaginationData((prev) => ({ ...prev, page: newPage, rowsPerPage }));
    fetchData(
      filters.smartSourceLensFiltersData,
      newPage,
      sortBy,
      sortOrder,
      selectedUOM,
      rowsPerPage,
      fieldsection,
    );
  };

  const loadFiltersApi = async (
    basePayload,
    loadIntentId,
    shouldShowShimmer,
    prevFiltersData,
  ) => {
    if (shouldShowShimmer) {
      setFiltersLoading(true);
    }

    const signal = cancelPrevious("filters");

    try {
      const isDcSupplyInsights = activeTab === "CBU-DC-Source";

      // Determine which filters API to call based on activeTab
      const filtersApiCall = isDcSupplyInsights
        ? fetchDcSupplyInsightsFilters
        : fetchSmartSourceLensFilters;

      // Prepare payload with correct type and overviewtype
      const filtersPayload = {
        ...basePayload,
        type: isDcSupplyInsights ? "Dc Supply Insights" : "Smart Source Lens",
        overviewtype: isDcSupplyInsights
          ? "Summary"
          : getBackendTabValue(basePayload.overviewtype),
      };

      const res = await filtersApiCall(filtersPayload, signal);

      if (loadIntentId !== latestLoadIntentRef.current) return;

      setFilters((prev) => ({
        ...prev,
        smartSourceLensFiltersData: mergeFiltersResponse(
          res.filters,
          prevFiltersData,
        ),
        smartSourceLensColumnsMap: res.columns || {},
      }));
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error(error);
      }
    } finally {
      if (loadIntentId === latestLoadIntentRef.current) {
        if (!isInitialLoadDone) {
          setFiltersLoading(false);
          setIsInitialLoadDone(true);
        } else if (shouldShowShimmer) {
          setFiltersLoading(false);
        }
      }
    }
  };

  const loadAllApis = async (
    currentFilters = [],
    uom = selectedUOM,
    options = DEFAULT_LOAD_ALL_APIS_OPTIONS,
  ) => {
    const loadIntentId = ++latestLoadIntentRef.current;

    clearDeferredApiTimers();

    const parsedFilters = buildSelectedFilterPayloadForTab(
      activeTab,
      currentFilters,
    );

    // For filters API, use "Summary" overviewtype for CBU-DC-Source, CBU-Source, and Production Plan
    const filtersOverviewType = [
      "CBU-DC-Source",
      "CBU-Source",
      "Production Plan",
    ].includes(activeTab)
      ? "Summary"
      : getBackendTabValue(activeTab);

    const basePayload = {
      email: activeAccount.username,
      uom,
      filters: parsedFilters,
      type: "Smart Source Lens",
      overviewtype: filtersOverviewType,
    };

    try {
      setLoading((prev) => ({ ...prev, dataLoading: true }));

      /**
       * 1. Filters API
       * Keep this independent. It can finish whenever.
       */
      if (options.skipFilters) {
        setFiltersLoading(false);
      } else {
        const shouldShowShimmer =
          !isInitialLoadDone || options.showFilterShimmer;

        loadFiltersApi(
          basePayload,
          loadIntentId,
          shouldShowShimmer,
          currentFilters,
        );
      }

      /**
       * 2. Overview table first.
       * This is what user cares about immediately.
       */
      fetchData(
        currentFilters,
        1,
        "",
        "DESC",
        uom,
        paginationData?.rowsPerPage || 15,
        "",
      );

      /**
       * 3. KPI slightly later.
       * Avoid starting KPI at exact same moment as rows/count/totals.
       */
      if (!options.skipKpi) {
        scheduleKpiApi(currentFilters, uom, loadIntentId);
      }

      /**
       * 4. Heavy APIs much later.
       * These were the biggest queue builders in your Network tab.
       */
      if (!options.skipHeavyApis) {
        scheduleHeavyApis(currentFilters, uom, loadIntentId);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading((prev) => ({ ...prev, initialLoading: false }));
    }
  };

  // Toggle More Filters drawer
  const toggleFilterDrawer = useCallback(() => {
    setIsDrawerOpen((prev) => ({ ...prev, filterDrawer: !prev.filterDrawer }));
  }, []);

  // Toggle Customize Columns drawer
  const toggleCustomizeDrawer = useCallback(() => {
    setIsDrawerOpen((prev) => ({
      ...prev,
      customiseDrawer: !prev.customiseDrawer,
    }));
  }, []);

  // Handle filter selection changes
  const handleFilterChange = (label, selectedOptions) => {
    const updatedFilters = filters.smartSourceLensFiltersData.map((filter) =>
      filter.label === label
        ? { ...filter, selectedData: selectedOptions }
        : filter,
    );

    setFilters((prev) => ({
      ...prev,
      smartSourceLensFiltersData: updatedFilters,
    }));

    // Debounced: rapid multi-select bursts collapse into ONE server
    // fan-out. See `scheduleLoadAllApis` comment for rationale.
    scheduleLoadAllApis(updatedFilters, selectedUOM, {
      skipHeavyApis: false,
      skipFilters: false,
      skipKpi: false,
    });
  };

  // Handle UOM change
  const handleUOMChange = (label, selectedOptions = "Tonnes") => {
    setSelectedUOM(selectedOptions);

    scheduleLoadAllApis(
      filters.smartSourceLensFiltersData,
      selectedOptions,
      {
        skipHeavyApis: false,
        skipFilters: false,
        skipKpi: false,
      },
      500,
    );
  };

  // Reset all filters - clear selectedData
  const resetFilter = () => {
    const cleared = filters.smartSourceLensFiltersData.map((filter) => ({
      ...filter,
      selectedData: [],
    }));

    setFilters((prev) => ({
      ...prev,
      smartSourceLensFiltersData: cleared,
    }));

    setSelectedUOM("Tonnes");

    // Clear weightage state so the overview table payload does not
    // include Weightage Percentage / top_n after reset
    weightageSubmittedRef.current = false;
    setWeightage(DEFAULT_WEIGHTAGE);
    setTopNValue(15);
    setWeightageError("");

    // Reset PDQ sorting to default
    setPdqSortingData(DEFAULT_PDQ_SORTING);

    // use defined variable
    scheduleLoadAllApis(
      cleared,
      "Tonnes",
      {
        skipHeavyApis: false,
        skipFilters: false,
        skipKpi: false,
      },
      300,
    );
  };

  // Remove a single selected value from a favourite filter (chip cancel button).
  // Updates state, then re-triggers all APIs (filters + KPI + overview + PDQ + historical)
  // with the updated payload where the removed value is no longer sent.
  const handleRemoveSelectedValue = (label, valueToRemove) => {
    const updatedFilters = filters.smartSourceLensFiltersData.map((filter) => {
      if (filter.label !== label) return filter;
      const current = getSelectedValues(filter.selectedData);
      return {
        ...filter,
        selectedData: current.filter(
          (v) => String(v) !== String(valueToRemove),
        ),
      };
    });

    setFilters((prev) => ({
      ...prev,
      smartSourceLensFiltersData: updatedFilters,
    }));

    // Debounced: users often remove several chips in a row; collapse
    // that burst into a single server fan-out.
    scheduleLoadAllApis(updatedFilters, selectedUOM, {
      skipHeavyApis: false,
      skipFilters: false,
      skipKpi: false,
    });
  };

  const handleExpand = (type) => {
    setExpandedGraph(type);
    setShowExpanded(false);
  };

  const handleCloseExpanded = () => {
    setExpandedGraph(null);
    setShowExpanded(true);
  };

  // Logic to handle customization
  const handleCustomization = () => {
    toggleCustomizeDrawer();
  };

  const closeSnackbar = useCallback(() => {
    setSnackBarObject((prev) => ({ ...prev, snackbarOpen: false }));
  }, []);

  const currentColumns =
    filters.customizedColumnsMap?.[activeTab] ||
    filters.smartSourceLensColumnsMap?.[activeTab] ||
    fallbackColumns ||
    [];

  // Logic to export table data to Excel
  const handleExportToExcel = async () => {
    setLoading((prev) => ({
      ...prev,
      exportLoading: true,
    }));

    try {
      const isDcSupplyInsights = activeTab === "CBU-DC-Source";

      const payload = {
        email: activeAccount.username,
        uom: selectedUOM,

        filters: buildSelectedFilterPayloadForTab(
          activeTab,
          filters.smartSourceLensFiltersData,
        ),

        type: isDcSupplyInsights ? "Dc Supply Insights" : "Smart Source Lens",

        overviewtype: isDcSupplyInsights
          ? "Summary"
          : getBackendTabValue(activeTab),

        pagination: {
          page: paginationData?.page || 1,
          rowsPerPage: paginationData?.rowsPerPage || DEFAULT_ROWS_PER_PAGE,
        },

        sorting: {
          sortBy: sortingData.sortBy || "",
          sortOrder: sortingData.sortOrder || "DESC",
          fieldsection: sortingData.fieldsection || "",
        },
      };

      if (
        shouldSendWeightagePayload(activeTab, weightageSubmittedRef.current)
      ) {
        payload["Weightage Percentage"] = {
          Volume: Number(weightage.Volume),
          Value: Number(weightage.Value),
        };

        payload.top_n = Number(topNValue);
      }

      if (isDcSupplyInsights) {
        await fetchDcSupplyInsightsExportData(payload, activeTab);
      } else {
        await fetchSmartScourceLensExportData(payload, activeTab);
      }

      setSnackBarObject({
        snackbarOpen: true,
        message: "File exported successfully",
        type: "success",
      });
    } catch (error) {
      console.error("Error exporting data:", error);

      setSnackBarObject({
        snackbarOpen: true,
        message: "Error occurred while exporting file.",
        type: "error",
      });
    } finally {
      setLoading((prev) => ({
        ...prev,
        exportLoading: false,
      }));
    }
  };

  const selectedNonFavChips = useMemo(
    () =>
      filters.smartSourceLensFiltersData
        .filter((filter) => !filter.isFavourite)
        .flatMap((filter) =>
          getSelectedValues(filter.selectedData).map((value) => ({
            label: filter.label,
            value,
          })),
        ),
    [filters.smartSourceLensFiltersData],
  );

  return (
    <>
      {loading.initialLoading ? (
        <div className={styles.loaderContainer}>
          <CircularProgress />
        </div>
      ) : (
        <div
          className={`${styles.container} ${loading.exportLoading ? styles.blurBackground : ""}`}
        >
          {/* Sticky Header Section: KPI, Filters, and Selected Chips */}
          <div className={styles.stickyHeaderSection}>
            <SmartSourceLensKpi kpiData={kpiData} loading={kpiLoading} />
            <div className={styles.sectionContainer}>
            {filtersLoading ? (
              <div className={styles.filterShimmerWrapper}>
                {FILTER_SHIMMER_KEYS.map((key) => (
                  <div key={key} className={styles.filterShimmer} />
                ))}
              </div>
            ) : (
              <>
                {filters["smartSourceLensFiltersData"]
                  .filter((filter) => filter.isFavourite)
                  .map((filter, index) => (
                    <div
                      key={`${filter.label}-${index}`}
                      className={styles.filterWrapper}
                    >
                      <MultiSelectWithSearchInput
                        key={`${filter.label}-${index}`}
                        label={filter.label}
                        options={filter.data}
                        filterValues={filter.selectedData}
                        isMultiSelect={filter.isMultiSelect}
                        handleFilter={handleFilterChange}
                        type="Smart Source Lens"
                        defaultValue={filter.defaultValue || []}
                      />
                    </div>
                  ))}

                {/* UOM Filter */}
                <div className={styles.filterWrapper}>
                  <MultiSelectWithSearchInput
                    label="Units"
                    options={uomOptions}
                    filterValues={[selectedUOM]}
                    isMultiSelect={false}
                    handleFilter={handleUOMChange}
                  />
                </div>
                <div className={styles.iconContainer}>
                  <button
                    type="button"
                    className={styles.iconButton}
                    aria-label="Refresh filters"
                    onClick={resetFilter}
                  >
                    <img src={Refresh} className={styles.iconStyling} alt="" />
                  </button>
                  <OverflowMenu
                    menuItems={[
                      {
                        label: "More Filters",
                        icon: Filters,
                        onClick: toggleFilterDrawer,
                      },
                    ]}
                    Icon={Threedots}
                  />
                </div>
              </>
            )}
          </div>

          {selectedNonFavChips.length > 0 && (
            <div className={styles.selectedChipsRow}>
              {selectedNonFavChips.map((chip) => (
                <div
                  key={`${chip.label}-${String(chip.value)}`}
                  className={styles.selectedChip}
                  title={`${chip.label}: ${chip.value}`}
                >
                  <span className={styles.selectedChipLabel}>
                    {chip.label}:
                  </span>
                  <span className={styles.selectedChipValue}>
                    {String(chip.value)}
                  </span>
                  <button
                    type="button"
                    className={styles.selectedChipClose}
                    aria-label={`Remove ${chip.label} ${chip.value}`}
                    onClick={() =>
                      handleRemoveSelectedValue(chip.label, chip.value)
                    }
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
          </div>
          {/* End Sticky Header Section */}

          {/* Scrollable Content Section */}
          <div className={styles.scrollableContentSection}>
          {!loading.initialLoading && (
            <div className={styles.placeholderWrapper}>
              <div className={styles.card}>
                <div className={styles.header}>
                  <span className={styles.titleName}>
                    Buckets of Allocation to PDQ
                  </span>
                  {showExpanded && (
                    <div className={styles.segmentedBtn}>
                      <button
                        type="button"
                        className={styles.segment}
                        style={{
                          padding: "0px",
                          background: "white",
                          cursor: "pointer",
                          position: "relative",
                        }}
                        title="Expand / Zoom"
                        onClick={() => handleExpand("buckets_allocation")}
                      >
                        <img
                          src={Expand}
                          alt="Expand"
                          className={styles.expandIconCss}
                        />
                      </button>
                    </div>
                  )}
                </div>
                <BucketsOfAllocation
                  apiData={{ data: pdqTableData }}
                  loading={pdqLoading}
                  loadMoreLoading={pdqLoadMoreLoading}
                  hasMore={pdqHasMore}
                  onLoadMore={handlePdqLoadMore}
                  showExpanded={showExpanded}
                  sortingData={pdqSortingData}
                  onSortChange={handlePdqSortChange}
                />
              </div>

              <div className={styles.card}>
                <div className={styles.header}>
                  <span className={styles.titleName}>
                    Historical data (NR to PDQ)
                  </span>
                  {showExpanded && (
                    <div className={styles.segmentedBtn}>
                      <button
                        type="button"
                        className={styles.segment}
                        style={{
                          padding: "0px",
                          background: "white",
                          cursor: "pointer",
                          position: "relative",
                        }}
                        title="Expand / Zoom"
                        onClick={() => handleExpand("historical_graph")}
                      >
                        <img
                          src={Expand}
                          alt="Expand"
                          className={styles.expandIconCss}
                        />
                      </button>
                    </div>
                  )}
                </div>
                <HistoricalDataGraph
                  data={historicalData}
                  loading={historicalLoading}
                />
              </div>
            </div>
          )}

          {expandedGraph === "buckets_allocation" && (
            <div className={styles.expandedOverlay}>
              <div className={styles.expandedContainer}>
                {/* Header with Close Button */}
                <div className={styles.expandedHeader}>
                  <span>Buckets of Allocation to PDQ</span>
                  <button
                    type="button"
                    className={styles.closeButton}
                    onClick={handleCloseExpanded}
                  >
                    ✕
                  </button>
                </div>

                {/* Table */}
                <div className={styles.expandedTableWrapper}>
                  <BucketsOfAllocation
                    apiData={{ data: pdqTableData }}
                    loading={pdqLoading}
                    loadMoreLoading={pdqLoadMoreLoading}
                    hasMore={pdqHasMore}
                    onLoadMore={handlePdqLoadMore}
                    sortingData={pdqSortingData}
                    onSortChange={handlePdqSortChange}
                  />
                </div>
              </div>
            </div>
          )}

          {expandedGraph === "historical_graph" && (
            <div className={styles.expandedOverlay}>
              <div className={styles.expandedContainer}>
                {/* Header with Close Button */}
                <div className={styles.expandedHeader}>
                  <span>Historical data (NR to PDQ)</span>
                  <button
                    type="button"
                    className={styles.closeButton}
                    onClick={handleCloseExpanded}
                  >
                    ✕
                  </button>
                </div>

                {/* Table */}
                <div className={styles.expandedTableWrapper}>
                  <HistoricalDataGraph
                    data={historicalData}
                    loading={historicalLoading}
                  />
                </div>
              </div>
            </div>
          )}

          <div className={styles.intrMainBody}>
            <div className={styles.intrHeader}>
              <div>Overview</div>

              <div className={styles.customButtonGroup}>
                {(activeTab === "Manual Indent" ||
                  activeTab === "Production Plan") && (
                  <div className={styles.simpleTooltip}>
                    <button
                      type="button"
                      onClick={handleWeightageDialogOpen}
                      style={{
                        padding: "10px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "12px",
                        border: "none",
                        background: "#3b4cca",
                        color: "#ffffff",
                        fontWeight: 600,
                      }}
                    >
                      Weighted Average &amp; Top N
                    </button>
                    <span className={styles.tooltipText}>
                      Weighted Average defines priority score using Count,
                      Volume & Value.
                      <br />
                      Top N controls how many high-priority records are shown.
                    </span>
                  </div>
                )}
                <OverviewTabs
                  selectedTab={activeTab}
                  onTabChange={setActiveTab}
                />
                <CustomButton
                  icon={Expand}
                  label="Customise"
                  type="mm-save"
                  onClick={handleCustomization}
                />
                <CustomButton
                  icon={newExcel}
                  label="Export"
                  type="mm-save"
                  onClick={handleExportToExcel}
                />
                <div className={styles.segmentedBtnOverview}>
                  <button
                    type="button"
                    className={styles.segment}
                    style={{
                      padding: "6px",
                      background: "white",
                      cursor: "pointer",
                    }}
                    onClick={() => handleExpand("table")}
                    title="Expand / Zoom"
                  >
                    <img
                      src={Expand}
                      alt="Expand"
                      className={styles.expandIconOverview}
                    />
                  </button>
                </div>
              </div>
            </div>
            <SmartSourceLensOverviewTable
              data={tableData}
              columns={currentColumns.length ? currentColumns : null}
              paginationData={paginationData}
              sortingData={sortingData}
              handlePageChange={handlePageChange}
              isLoading={loading.dataLoading || loading.tableBodyLoading}
              activeTab={activeTab}
            />
          </div>
          </div>
          {/* End Scrollable Content Section */}

          {isDrawerOpen.filterDrawer && (
            <MoreFilters
              anchor="right"
              filterDrawer={isDrawerOpen.filterDrawer}
              handleFilterDrawer={toggleFilterDrawer}
              rmpmFilters={filters["smartSourceLensFiltersData"]}
              type={getFilterPreferenceType(activeTab)}
              setRmpmFilters={(updatedFilters) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  smartSourceLensFiltersData: updatedFilters,
                }));

                scheduleLoadAllApis(
                  updatedFilters,
                  selectedUOM,
                  {
                    skipHeavyApis: false,
                    skipFilters: false,
                    skipKpi: false,
                  },
                  300,
                );
              }}
              username={activeAccount.username}
              limit={8}
            />
          )}

          {isDrawerOpen.customiseDrawer && (
            <CustomiseColumns
              anchor="right"
              columns={[...currentColumns]}
              isOpen={isDrawerOpen.customiseDrawer}
              handleCustomizeDrawer={toggleCustomizeDrawer}
              columnCustomization={(customizedColumns) => {
                setFilters((prev) => ({
                  ...prev,
                  customizedColumnsMap: {
                    ...prev.customizedColumnsMap,
                    [activeTab]: customizedColumns, //store per tab
                  },
                }));

                // After successful saveColumnPreference, refetch overview
                // so table reflects latest backend response immediately.
                fetchData(
                  filters.smartSourceLensFiltersData,
                  paginationData?.page || 1,
                  sortingData.sortBy || "",
                  sortingData.sortOrder || "DESC",
                  selectedUOM,
                  paginationData?.rowsPerPage || 15,
                  sortingData.fieldsection || "",
                );

                toggleCustomizeDrawer();
              }}
              username={activeAccount.username}
              type={getFilterPreferenceType(activeTab)}
            />
          )}
        </div>
      )}

      {loading.exportLoading && (
        <div className={styles.exportOverlay}>
          <div className={styles.exportLoader}>
            <CircularProgress />
            <div style={{ marginTop: 10, fontSize: 14 }}>Exporting file...</div>
          </div>
        </div>
      )}
      {expandedGraph === "table" && (
        <div className={styles.expandedOverlay}>
          <div className={styles.expandedContainer}>
            {/* Header with Close Button */}
            <div className={styles.expandedHeader}>
              <span>Smart Source lens Overview</span>
              <button
                type="button"
                className={styles.closeButton}
                onClick={handleCloseExpanded}
              >
                ✕
              </button>
            </div>

            {/* Table */}
            <div className={styles.expandedTableWrapper}>
              <SmartSourceLensOverviewTable
                data={tableData}
                columns={currentColumns.length ? currentColumns : null}
                paginationData={paginationData}
                sortingData={sortingData}
                handlePageChange={handlePageChange}
                isLoading={loading.dataLoading || loading.tableBodyLoading}
                activeTab={activeTab}
              />
            </div>
          </div>
        </div>
      )}
      {snackBarObject.snackbarOpen && (
        <SnackBarMessage
          type={snackBarObject.type}
          snackbarMessage={snackBarObject.message}
          snackbarOpen={snackBarObject.snackbarOpen}
          setSnackbarOpen={closeSnackbar}
        />
      )}
      <WeightageTopNDialog
        open={openWeightageDialog}
        onClose={handleWeightageDialogClose}
        weightage={weightage}
        setWeightage={setWeightage}
        topNValue={topNValue}
        setTopNValue={setTopNValue}
        weightageError={weightageError}
        validateWeightage={validateWeightage}
        dialogLoading={dialogLoading}
        onSubmit={handleWeightageSubmit}
      />
    </>
  );
};

SmartSourceLens.propTypes = {
  sideViewTab: PropTypes.string,
};

SmartSourceLens.defaultProps = {
  sideViewTab: "",
};

export default SmartSourceLens;
